import { useEffect, useState, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { api, ACCOUNT_COLORS, ACCOUNT_LABEL } from "@/lib/api";
import { clearActiveLeague } from "@/lib/league";
import { setDeviceUserId } from "@/lib/league";
import PinModal from "@/components/PinModal";
import ClaimProfileModal from "@/components/ClaimProfileModal";
import ChallengeBanner from "@/components/ChallengeBanner";
import BrandLogo from "@/components/BrandLogo";
import HowToPlayModal from "@/components/HowToPlayModal";
import CoronationBanner from "@/components/CoronationBanner";
import { toast } from "sonner";

const TROPHY = "https://static.prod-images.emergentagent.com/jobs/1b0b3fd3-e19a-43a9-907c-68e9e32f9aea/images/3dceae09e59e2fecf11ccb3e586ca15ed7cd346b32adaa375ab18d6ccbb2171b.png";

export default function SwitchProfile({ league }) {
  const nav = useNavigate();
  const [users, setUsers] = useState([]);
  const [pinUser, setPinUser] = useState(null);
  const [claimUser, setClaimUser] = useState(null);
  const [challenge, setChallenge] = useState(null);
  const [showHowTo, setShowHowTo] = useState(false);
  const codeCopyRef = useRef(null);

  useEffect(() => {
    api.listUsers(league.id).then(setUsers);
    api.getChallengeInfo().then(setChallenge).catch(() => {});
    const t = setInterval(() => {
      api.listUsers(league.id).then(setUsers).catch(() => {});
    }, 10000);
    return () => clearInterval(t);
  }, [league.id]);

  const tap = (u) => {
    // V2 — Profile that has no PIN yet → "Claim Profile" flow
    if (!u.has_pin) {
      setClaimUser(u);
      return;
    }
    setPinUser(u);
  };

  const copyCode = async () => {
    if (!league.code) return;
    try {
      await navigator.clipboard.writeText(league.code);
      toast.success(`Code ${league.code} copied`);
    } catch {
      toast.error("Couldn't copy");
    }
  };

  const switchLeague = () => {
    if (window.confirm("Leave this league and return to the welcome screen? (Your data stays — only this device switches.)")) {
      clearActiveLeague();
      nav("/welcome");
    }
  };

  const active = league.status === "active";

  return (
    <div className="min-h-screen mobile-bottom-pad">
      {/* Phase 4.2 — Coronation banner (edge-to-edge, top of viewport) */}
      <CoronationBanner leagueId={league.id} />

      {/* Slim top header banner */}
      <header className="sticky top-0 z-40 backdrop-blur-2xl bg-black/70 border-b border-white/10 safe-top">
        <div className="max-w-[1600px] mx-auto px-4 md:px-12 py-3 md:py-5 flex items-center justify-between gap-3">
          <div className="flex items-center gap-3 min-w-0 flex-1">
            <BrandLogo size="sm" showWordmark={false} className="md:hidden flex-shrink-0" />
            <BrandLogo size="sm" className="hidden md:flex flex-shrink-0" />
            <span className="label-mono hidden md:inline-block text-[var(--stakes-muted)]">/</span>
            <div className="font-anton text-lg sm:text-xl md:text-2xl uppercase truncate min-w-0" data-testid="header-league-name">
              {league.name}
            </div>
          </div>
          <div className="flex items-center gap-2">
            {league.code && (
              <button
                ref={codeCopyRef}
                onClick={copyCode}
                className="label-mono items-center gap-1 px-2 sm:px-3 h-9 border border-[var(--stakes-border)] hover:border-[var(--neon-blue)] hover:text-[var(--neon-blue)] hover-flip flex"
                data-testid="header-code-button"
                title="Tap to copy league code"
              >
                #{league.code}
              </button>
            )}
            <span
              className="label-mono px-2 sm:px-3 py-1 border hidden sm:inline-block"
              style={{
                borderColor: active ? "var(--stakes-primary)" : "var(--stakes-border)",
                color: active ? "var(--stakes-primary)" : "var(--stakes-muted)",
              }}
              data-testid="league-status-badge"
            >
              ● {active ? "LIVE" : "DRAFT"}
            </span>
            <button
              onClick={() => nav("/setup")}
              className="label-mono px-2 sm:px-3 h-9 border border-[var(--stakes-admin)] text-[var(--stakes-admin)] hover:bg-[var(--stakes-admin)] hover:text-black hover-flip"
              data-testid="header-admin-button"
              title="Admin Control Panel (PIN protected)"
            >
              ⚙
            </button>
          </div>
        </div>
      </header>

      {/* Hero strip (compact on mobile) */}
      <section className="border-b border-[var(--stakes-border)] hidden md:block">
        <div className="max-w-[1600px] mx-auto px-8 lg:px-12 py-8 grid lg:grid-cols-12 gap-8 items-end">
          <div className="lg:col-span-8 animate-slide-up">
            <div
              className="label-mono"
              style={{ color: active ? "var(--stakes-primary)" : "var(--stakes-muted)" }}
            >
              {active ? "LEAGUE IS LIVE — STAKES ARE BINDING" : "DRAFT MODE"}
            </div>
            <h1 className="font-anton text-4xl sm:text-5xl lg:text-7xl uppercase leading-[0.85] mt-2 wrap-words break-words">
              {league.name}
            </h1>
            <div className="flex flex-wrap gap-8 mt-4 font-barlow text-lg uppercase">
              <div>
                <div className="label-mono">ADMIN</div>
                <div className="text-xl font-bold">{league.admin_name}</div>
              </div>
              <div>
                <div className="label-mono">PRIZE</div>
                <div className="text-xl font-bold wrap-words max-w-md">{league.monthly_prize || "—"}</div>
              </div>
              <div>
                <div className="label-mono">PLAYERS</div>
                <div className="text-xl font-bold">{users.length}</div>
              </div>
              {league.code && (
                <button onClick={copyCode} className="text-left">
                  <div className="label-mono">LEAGUE CODE</div>
                  <div className="text-xl font-bold tracking-[0.2em] hover:text-[var(--neon-blue)]">#{league.code}</div>
                </button>
              )}
            </div>
          </div>
          <div className="lg:col-span-4 flex justify-end">
            <img src={TROPHY} alt="Prize" className="h-36 object-contain" />
          </div>
        </div>
      </section>

      {/* Challenge banner */}
      {challenge?.is_challenge_day && (
        <div className="max-w-[1600px] mx-auto px-4 md:px-12 pt-4 md:pt-8">
          <ChallengeBanner info={challenge} compact />
        </div>
      )}

      {/* Mobile Lobby — roster ONLY (Phase 19 strict pre-login privacy) */}
      <div className="md:hidden max-w-[1600px] mx-auto px-4 py-4">
        <MobileProfiles
          users={users}
          tap={tap}
          league={league}
          onHowTo={() => setShowHowTo(true)}
          onSwitchLeague={switchLeague}
          onRefresh={async () => {
            try {
              const [l, u] = await Promise.all([
                api.getLeagueById(league.id),
                api.listUsers(league.id),
              ]);
              setUsers(u);
              api.getChallengeInfo().then(setChallenge).catch(() => {});
              toast.success(`Synced ${u.length} players from cloud`);
              void l;
            } catch {
              toast.error("Couldn't refresh — check connection");
            }
          }}
        />
      </div>

      {/* Desktop Lobby — roster ONLY (no leaderboard / no feed pre-login) */}
      <section className="hidden md:block max-w-[1600px] mx-auto px-8 lg:px-12 py-10">
        <div className="label-mono mb-4">CHOOSE YOUR PROFILE — TAP TO ENTER YOUR PIN</div>
        <ProfileGrid users={users} onTap={tap} variant="desktop" />
        <div className="mt-6 flex gap-3 flex-wrap">
          <button
            onClick={() => setShowHowTo(true)}
            className="label-mono px-3 h-10 border border-[var(--stakes-border)] hover:bg-white hover:text-black hover-flip"
            data-testid="desktop-howto-button"
          >
            ❓ HOW TO PLAY
          </button>
          <button
            onClick={switchLeague}
            className="label-mono px-3 h-10 border border-[var(--stakes-border)] hover:border-[var(--stakes-primary)] hover:text-[var(--stakes-primary)] hover-flip"
            data-testid="desktop-switch-league"
          >
            ⤺ SWITCH LEAGUE
          </button>
        </div>
      </section>

      {/* Phase 19 — BottomNav intentionally REMOVED from the Lobby. Users
          must enter their PIN to reveal any tabs (Home / Leaderboard / Proof
          Wall). The PinModal below is the only path forward. */}

      <PinModal
        user={pinUser}
        open={!!pinUser}
        onClose={() => setPinUser(null)}
        onSuccess={(u, opts) => {
          setPinUser(null);
          if (opts?.remember) setDeviceUserId(u.id);
          nav(`/dashboard/${u.id}`);
        }}
      />

      {/* V2 — Self-serve Claim Profile (when player has no PIN yet) */}
      <ClaimProfileModal
        user={claimUser}
        open={!!claimUser}
        onClose={() => setClaimUser(null)}
        onClaimed={(updated) => {
          // Refresh the local roster so the card flips to "PIN" badge
          setUsers((arr) => arr.map((x) => (x.id === updated.id ? updated : x)));
          setClaimUser(null);
          // Carry the device-binding straight into the dashboard
          setDeviceUserId(updated.id);
          nav(`/dashboard/${updated.id}`);
        }}
      />

      <HowToPlayModal open={showHowTo} onClose={() => setShowHowTo(false)} />

      {/* Phase 17 — system timezone visibility footer */}
      <footer className="px-4 md:px-12 pb-24 pt-6 label-mono text-[var(--stakes-muted)] flex flex-wrap items-center justify-between gap-2">
        <span>STAKES · MULTI-LEAGUE</span>
        <span data-testid="switchprofile-system-timezone" className="font-mono uppercase">
          SYSTEM TIMEZONE · {league.timezone || "UTC"}
        </span>
      </footer>
    </div>
  );
}

function ProfileGrid({ users, onTap, variant }) {
  if (users.length === 0) {
    return (
      <div className="border border-dashed border-[var(--stakes-border)] p-8 text-center">
        <div className="label-mono mb-2">NO PLAYERS YET</div>
      </div>
    );
  }
  return (
    <div
      className="grid gap-3 sm:gap-4"
      style={
        variant === "mobile"
          ? { gridTemplateColumns: "repeat(2, minmax(0, 1fr))" }
          : { gridTemplateColumns: "repeat(auto-fill, minmax(220px, 1fr))" }
      }
      data-testid="profile-gallery"
    >
      {users.map((u, idx) => {
        const color = ACCOUNT_COLORS[u.account_type];
        const initials = u.name.split(" ").map((w) => w[0]).slice(0, 2).join("").toUpperCase();
        const hasPin = !!u.has_pin;
        const negative = u.points_total < 0;
        return (
          <button
            key={u.id}
            onClick={() => onTap(u)}
            className={`group relative border border-[var(--stakes-border)] bg-[var(--stakes-surface)] text-left p-3 sm:p-4 flex flex-col justify-between hover:border-white hover-flip animate-slide-up ${
              variant === "mobile" ? "h-40" : "h-64 lg:h-72 p-5"
            }`}
            style={{ animationDelay: `${idx * 30}ms` }}
            data-testid={`user-profile-card-${u.id}`}
          >
            <div className="absolute top-0 left-0 right-0 h-1" style={{ background: color }} />
            <div>
              <div className="label-mono flex items-center gap-1 flex-wrap" style={{ color }}>
                {ACCOUNT_LABEL[u.account_type]} {hasPin && <span>• PIN</span>}
              </div>
              <div
                className={`mt-2 flex items-center justify-center font-anton ${variant === "mobile" ? "w-12 h-12 text-2xl" : "w-20 h-20 text-4xl mt-3"}`}
                style={{ background: color, color: "#000" }}
              >
                {initials || "?"}
              </div>
            </div>
            <div>
              <div className={`font-anton uppercase leading-none wrap-words ${variant === "mobile" ? "text-lg" : "text-2xl lg:text-3xl"}`}>
                {u.name}
              </div>
              <div className="mt-2 flex items-end justify-between">
                <div>
                  <div className="label-mono">PTS</div>
                  <div
                    className={`font-anton leading-none ${variant === "mobile" ? "text-2xl" : "text-3xl lg:text-4xl"}`}
                    style={{ color: negative ? "var(--stakes-primary)" : "#fff" }}
                    data-testid={`user-card-points-${u.id}`}
                  >
                    {u.points_total > 0 ? "+" : ""}{u.points_total}
                  </div>
                </div>
                <div className="label-mono group-hover:text-white text-[var(--stakes-muted)]" style={{ fontSize: "10px" }}>
                  ENTER →
                </div>
              </div>
            </div>
          </button>
        );
      })}
    </div>
  );
}

function MobileProfiles({ users, tap, league, onHowTo, onSwitchLeague, onRefresh }) {
  return (
    <div className="space-y-4">
      {/* Compact league header card */}
      <div className="border border-[var(--stakes-border)] bg-[var(--stakes-surface)] p-3 flex items-center gap-3">
        <img src={TROPHY} alt="" className="w-12 h-12 object-contain flex-shrink-0" />
        <div className="flex-1 min-w-0">
          <div className="label-mono">PRIZE</div>
          <div
            className="font-barlow font-bold text-base uppercase wrap-words leading-snug"
            data-testid="mobile-prize-text"
          >
            {league.monthly_prize || "—"}
          </div>
        </div>
      </div>

      <div className="label-mono">SWITCH PROFILE — TAP TO ENTER</div>
      <ProfileGrid users={users} onTap={tap} variant="mobile" />

      <div className="grid grid-cols-2 gap-2 pt-2">
        <button
          onClick={onRefresh}
          className="label-mono px-3 h-11 border border-[var(--stakes-border)] hover:bg-white hover:text-black hover-flip"
          data-testid="mobile-refresh-button"
        >
          ↻ REFRESH
        </button>
        <button
          onClick={onHowTo}
          className="label-mono px-3 h-11 border border-[var(--stakes-border)] hover:bg-white hover:text-black hover-flip"
          data-testid="mobile-howto-button"
        >
          ❓ HOW TO PLAY
        </button>
        <button
          onClick={onSwitchLeague}
          className="col-span-2 label-mono px-3 h-11 border border-[var(--stakes-border)] hover:border-[var(--stakes-primary)] hover:text-[var(--stakes-primary)] hover-flip"
          data-testid="mobile-switch-league"
        >
          ⤺ SWITCH LEAGUE
        </button>
      </div>
    </div>
  );
}
